import React, {useEffect, useMemo, useState} from "react";
import {ArrowBigDown, ArrowBigUp} from "lucide-react";
import {toast} from "sonner";

import {AjaxDiscussionRestControllerApi} from "@/generated-client";
import {ClientFactory} from "@/main/common/ClientFactory";
import {isHandledAuthSessionError} from "@/main/common/AjaxAuthSessionHandler";
import {MetaHandler} from "@/main/common/MetaHandler";
import {VoteContainer} from "@/main/react/buttons/CtaButton";
import {ACCOUNT_ACCESS_DIALOG_COPY, withAccountAccessSurface} from "@/main/react/account_access/AccountAccessDialogContext";
import {
    rememberViewerActionRecord,
    resolveViewerActionRecord,
} from "@/main/common/ViewerActionRecords";
import {emitDiscussionViewerAgreementChanged} from "@/main/common/EventRegistry";
import {trackDiscussionVote} from "@/main/analytics/analyticsEvents";
import {
    agreementFromVoteState,
    applyVoteToState,
    DiscussionVoteState,
    parseDiscussionVoteState,
} from "@/main/common/DiscussionVoteState";
import {classifyDiscussionVoteFailure, logDiscussionVoteFailure} from "@/main/common/DiscussionVoteTelemetry";
import {Localizer} from "@/main/react/Types";

type CtaSize = "sm" | "md";

type Props = {
    discussionPublicId: string;
    initiallyAgreeing?: boolean;
    initiallyDisagreeing?: boolean;
    initialAgreementsCount?: number;
    initialDisagreementsCount?: number;
    localizer: Localizer;
    size?: CtaSize;
    disabled?: boolean;
    /** Analytics/account-access surface tag; defaults to the discussion-card react surface. */
    accountAccessSurface?: string;
};

/**
 * Standalone agree/disagree (thumbs up/down) vote control for a discussion, reusable outside the
 * full DiscussionButtons bar (e.g. compact recommendation-rail rows). Mirrors the vote logic in
 * DiscussionButtons: optimistic counts, the add/delete-agreement API, the viewer-agreement store,
 * the agreement-changed event, analytics, and calm error toasts.
 */
export default function DiscussionVoteButtons(props: Props) {
    const apiClient: AjaxDiscussionRestControllerApi = useMemo(() => ClientFactory.getAjaxDiscussionRestControllerApi(), []);
    const {localizer} = props;

    // The props describe the DTO this control was rendered from, which can be OLDER than the viewer's
    // own vote (a Back-restored feed item, a prefetched page), so the registry's record of what this tab
    // last saw the server confirm wins over them. See `ViewerActionRecords`.
    const seedVoteState = (): DiscussionVoteState => resolveViewerActionRecord("discussion-vote", props.discussionPublicId, {
        agreementsCount: props.initialAgreementsCount ?? 0,
        disagreementsCount: props.initialDisagreementsCount ?? 0,
        viewerAgreeing: Boolean(props.initiallyAgreeing),
        viewerDisagreeing: Boolean(props.initiallyDisagreeing),
    });

    const [agreementsCount, setAgreementsCount] = useState<number>(() => seedVoteState().agreementsCount);
    const [disagreementsCount, setDisagreementsCount] = useState<number>(() => seedVoteState().disagreementsCount);
    const [isAgreeing, setIsAgreeing] = useState<boolean>(() => seedVoteState().viewerAgreeing);
    const [isDisagreeing, setIsDisagreeing] = useState<boolean>(() => seedVoteState().viewerDisagreeing);
    const [isTogglingAgreement, setIsTogglingAgreement] = useState<boolean>(false);

    const reactAccountAccessDialog = withAccountAccessSurface(
        ACCOUNT_ACCESS_DIALOG_COPY.react,
        props.accountAccessSurface ?? "discussion_card_react",
    );

    const applyVoteState = (state: DiscussionVoteState): void => {
        setAgreementsCount(state.agreementsCount);
        setDisagreementsCount(state.disagreementsCount);
        setIsAgreeing(state.viewerAgreeing);
        setIsDisagreeing(state.viewerDisagreeing);
    };

    // Re-seed when this control is pointed at a different discussion. Same source as the mount seed, so
    // the overlay wins here too.
    useEffect(() => {
        applyVoteState(seedVoteState());
    }, [props.discussionPublicId]);

    const getStatus = (err: any) => err?.response?.status ?? err?.status;
    const getServerMessage = (err: any) => err?.response?.data?.message ?? err?.message;

    const toggleAgreement = async (mode: "AGREE" | "DISAGREE") => {
        if (props.disabled || !props.discussionPublicId) {
            return;
        }
        // In-flight guard: counts are mutated after an awaited request, so a fast second click would
        // double-apply the delta and desync the count. Matches DiscussionButtons.toggleAgreement.
        if (isTogglingAgreement) {
            return;
        }
        setIsTogglingAgreement(true);

        const id = props.discussionPublicId;
        const username = MetaHandler.getLoggedInUsername();
        const currentState: DiscussionVoteState = {
            agreementsCount,
            disagreementsCount,
            viewerAgreeing: isAgreeing,
            viewerDisagreeing: isDisagreeing,
        };
        // The optimistic prediction, computed BEFORE the request so the withdrawing branch below can be
        // chosen from the same snapshot the arithmetic used. Server truth replaces it when the endpoint
        // returns a usable body; a frontend deployed ahead of the backend falls back to this, never to
        // nothing.
        const optimisticState = applyVoteToState(currentState, mode);
        const isWithdrawing = (mode === "AGREE" && isAgreeing) || (mode === "DISAGREE" && isDisagreeing);
        try {
            const response = isWithdrawing
                ? await apiClient.deleteAgreementInDiscussion(id, {
                    headers: {"Content-Type": "application/json"},
                })
                : await apiClient.addAgreementInDiscussion(id, mode, {
                    headers: {"Content-Type": "application/json"},
                });
            if (!isWithdrawing) {
                trackDiscussionVote(mode === "AGREE" ? "upvote" : "downvote");
            }

            const nextState = parseDiscussionVoteState(response?.data) ?? optimisticState;
            const nextAgreement = agreementFromVoteState(nextState);
            applyVoteState(nextState);
            try {
                // Outlives this control, so a Back-restored card seeds from it rather than from the
                // pre-vote DTO the restoration cache holds.
                rememberViewerActionRecord("discussion-vote", id, nextState);
            } catch {
                // best-effort store update
            }
            try {
                emitDiscussionViewerAgreementChanged({discussionPublicId: id, agreement: nextAgreement, username});
            } catch {
                // best-effort cross-component sync
            }

            // The viewer's vote is a field of the cached discussion page-data response, which a hover
            // prefetch may already hold in its PRE-vote form. Drop it on success, or opening the
            // discussion within the 30 s TTL renders the vote as never cast. Prefetch design §2e.
            // Only the public id is available here — that is exactly why the cache carries an alias
            // map keyed off each response's own publicId/slug.
            ClientFactory.invalidateDiscussionPageData(id);
        } catch (e) {
            // EVERY branch logs before it returns. Until 2026-08-06 the three most diagnostic ones
            // (401/403/5xx) returned above the single logger call, so the only vote failures reaching
            // CloudWatch were the unclassified ones — and the session-loss branch, which reloads the
            // page and is therefore indistinguishable from "my vote vanished", never logged at all.
            const handledAuthSessionError = isHandledAuthSessionError(e);
            const status = getStatus(e);
            logDiscussionVoteFailure({
                discussionPublicId: id,
                mode,
                operation: isWithdrawing ? "remove" : "add",
                reason: classifyDiscussionVoteFailure(status, handledAuthSessionError),
                status,
                error: e,
            });

            if (handledAuthSessionError) {
                return;
            }
            if (status === 403) {
                toast.error(getServerMessage(e) || localizer("typescript.discussion.user_cannot_interact"));
                return;
            }
            if (status && status >= 500) {
                toast.error(localizer("typescript.discussion.server_error_generic"));
                return;
            }
            toast.error(localizer("typescript.discussion.participation.update_failed"));
        } finally {
            setIsTogglingAgreement(false);
        }
    };

    return (
        <VoteContainer
            leftButtonIcon={ArrowBigUp}
            leftButtonTitle={localizer("discussion.agreement")}
            leftButtonCount={agreementsCount}
            leftButtonActive={isAgreeing}
            leftButtonExtraClass="agreement-discussion-button"
            leftInteractionEffect="agree"
            leftCountFeedback="pulse"
            rightButtonIcon={ArrowBigDown}
            rightButtonTitle={localizer("discussion.disagreement")}
            rightButtonCount={disagreementsCount}
            rightButtonActive={isDisagreeing}
            rightButtonExtraClass="disagreement-discussion-button"
            rightInteractionEffect="disagree"
            rightCountFeedback="pulse"
            size={props.size ?? "sm"}
            localizer={localizer}
            displayTextOnMobile={false}
            disabled={props.disabled || isTogglingAgreement}
            requiresLogin={true}
            accountAccessDialog={reactAccountAccessDialog}
            onLeftClick={async (ev?: Event | React.MouseEvent<HTMLButtonElement>) => {
                try {
                    (ev as React.MouseEvent)?.preventDefault();
                    (ev as React.MouseEvent)?.stopPropagation();
                } catch {
                    // ignore
                }
                await toggleAgreement("AGREE");
            }}
            onRightClick={async (ev?: Event | React.MouseEvent<HTMLButtonElement>) => {
                try {
                    (ev as React.MouseEvent)?.preventDefault();
                    (ev as React.MouseEvent)?.stopPropagation();
                } catch {
                    // ignore
                }
                await toggleAgreement("DISAGREE");
            }}
        />
    );
}
